import { Alert, Button, Card, Modal, useToast } from '@catering/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import { Pencil, Save } from 'lucide-react';
import { type FormEvent, type JSX, type ReactNode, useState } from 'react';

import { ApiError } from '../../../api/client';
import { hasPermission } from '../../../auth/has-permission';
import { useCurrentUser } from '../../../auth/use-current-user';
import type { CustomerDetail, CustomerFormInput } from '../../../customers/api';
import {
  TAX_CONDITION_LABELS,
  ZONE_LABELS,
  deleteCustomer,
  reactivateCustomer,
  suspendCustomer,
  updateCustomer,
} from '../../../customers/api';
import type {
  CustomerFormFields,
  CustomerFormValues,
} from '../../../customers/customer-form-schema';
import { mapCreateError } from '../../../customers/customer-form-schema';
import { formatTaxId } from '../../../customers/format-tax-id';
import { CustomerForm } from '../customer-form-page';
import { SuspensionIndicator } from './suspension-indicator';

const customerDetailRoute = getRouteApi('/protected/customers/$customerId');

export interface CustomerDataTabProps {
  customer: CustomerDetail;
  onRefetch: () => void;
}

/**
 * `customers/spec.md` → "Pantallas de alta y detalle en back-office",
 * "Indicador de suspensión visible en el detalle". Actions map to
 * `customer.dto.ts`/`customers.controller.ts` from PR 2.
 */
export function CustomerDataTab({ customer, onRefetch }: CustomerDataTabProps): JSX.Element {
  const navigate = customerDetailRoute.useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const currentUser = useCurrentUser();
  const canUpdate = hasPermission(currentUser.data, 'customer.update');
  const canDeactivate = hasPermission(currentUser.data, 'customer.deactivate');

  const [mode, setMode] = useState<'view' | 'edit'>('view');
  const [suspendOpen, setSuspendOpen] = useState(false);
  const [conflictError, setConflictError] = useState<string | undefined>(undefined);

  const updateMutation = useMutation({
    mutationFn: (input: Partial<CustomerFormInput>) => updateCustomer(customer.id, input),
    onSuccess: async () => {
      await invalidate();
      setMode('view');
      toast('success', 'Empresa actualizada');
    },
  });

  const suspendMutation = useMutation({
    mutationFn: (reason: string) => suspendCustomer(customer.id, reason),
    onSuccess: async () => {
      await invalidate();
      setSuspendOpen(false);
      toast('success', 'Empresa suspendida');
    },
    onError: (error) => handleStatusConflict(error),
  });

  const reactivateMutation = useMutation({
    mutationFn: () => reactivateCustomer(customer.id),
    onSuccess: () => {
      void invalidate();
      toast('success', 'Empresa reactivada');
    },
    onError: (error) => handleStatusConflict(error),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteCustomer(customer.id),
    onSuccess: () => {
      toast('success', 'Empresa dada de baja');
      void navigate({ to: '/customers' });
    },
    onError: (error) => handleStatusConflict(error),
  });

  async function invalidate(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: ['customer', customer.id] });
    await queryClient.invalidateQueries({ queryKey: ['customers'] });
    onRefetch();
  }

  function handleStatusConflict(error: unknown): void {
    if (error instanceof ApiError && error.status === 403) {
      setConflictError('No tenés permiso para realizar esta acción.');
      return;
    }

    if (error instanceof ApiError && error.status === 409) {
      const code = (error.body as { code?: string } | null)?.code;

      if (code === 'CUSTOMER_STATUS_CONFLICT') {
        setConflictError('El estado de la empresa cambió. Volvé a intentarlo.');
        onRefetch();
        return;
      }
    }

    setConflictError('Ocurrió un error inesperado. Intentá de nuevo.');
  }

  if (mode === 'edit') {
    const { formError, fieldError } = mapCreateError(updateMutation.error);

    return (
      <Card>
        <CustomerForm
          submitLabel="Guardar cambios"
          submitIcon={Save}
          submitting={updateMutation.isPending}
          {...(formError === undefined ? {} : { formError })}
          {...(fieldError === undefined ? {} : { fieldError })}
          onCancel={() => setMode('view')}
          defaultValues={toFormFields(customer)}
          onSubmit={async (values) => {
            await updateMutation.mutateAsync(diffChanges(customer, values)).catch(() => undefined);
          }}
        />
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {conflictError !== undefined ? <Alert tone="danger">{conflictError}</Alert> : null}

      {customer.status === 'SUSPENDED' &&
      customer.suspensionReason !== null &&
      customer.suspendedAt !== null ? (
        <SuspensionIndicator
          reason={customer.suspensionReason}
          suspendedAt={customer.suspendedAt}
        />
      ) : null}

      <Card>
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-base font-semibold text-slate-900">Datos de la empresa</h2>
          {canUpdate ? (
            <Button variant="secondary" size="sm" icon={Pencil} onClick={() => setMode('edit')}>
              Editar
            </Button>
          ) : null}
        </div>

        <dl className="grid grid-cols-1 gap-x-8 gap-y-5 sm:grid-cols-2 lg:grid-cols-3">
          <Item label="Razón social" value={customer.legalName} />
          <Item label="Nombre comercial" value={customer.tradeName} />
          <Item label="CUIT" value={formatTaxId(customer.taxId)} />
          <Item label="Condición fiscal" value={TAX_CONDITION_LABELS[customer.taxCondition]} />
          <Item label="Email de facturación" value={customer.billingEmail} />
          <Item label="Zona" value={ZONE_LABELS[customer.zone]} />
          <Item label="Domicilio de facturación" value={formatAddress(customer)} />
          <Item label="Notas" value={customer.notes ?? '—'} />
        </dl>

        {canDeactivate ? (
        <div className="mt-6 flex flex-wrap items-center gap-3 border-t border-slate-100 pt-5">
          {customer.status === 'ACTIVE' ? (
            <Button variant="secondary" onClick={() => setSuspendOpen(true)}>
              Suspender
            </Button>
          ) : (
            <Button
              variant="secondary"
              loading={reactivateMutation.isPending}
              onClick={() => {
                if (window.confirm('¿Reactivar esta empresa?')) {
                  reactivateMutation.mutate();
                }
              }}
            >
              Reactivar
            </Button>
          )}
          <Button
            variant="destructive"
            loading={deleteMutation.isPending}
            onClick={() => {
              if (
                window.confirm(
                  '¿Dar de baja a esta empresa? Dejará de aparecer en el sistema.',
                )
              ) {
                deleteMutation.mutate();
              }
            }}
          >
            Dar de baja
          </Button>
        </div>
        ) : null}
      </Card>

      {suspendOpen ? (
        <SuspendDialog
          submitting={suspendMutation.isPending}
          onCancel={() => setSuspendOpen(false)}
          onConfirm={(reason) => suspendMutation.mutate(reason)}
        />
      ) : null}
    </div>
  );
}

function Item({ label, value }: { label: string; value: ReactNode }): JSX.Element {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-1 text-sm font-medium text-slate-900">{value}</dd>
    </div>
  );
}

function formatAddress(customer: CustomerDetail): string {
  const address = customer.billingAddress;

  if (address === null) {
    return '—';
  }

  return `${address.street} ${address.number}, ${address.city}, ${address.province} (${address.postalCode})`;
}

function toFormFields(customer: CustomerDetail): CustomerFormFields {
  return {
    legalName: customer.legalName,
    tradeName: customer.tradeName,
    taxId: customer.taxId,
    taxCondition: customer.taxCondition,
    billingEmail: customer.billingEmail,
    billingAddress: customer.billingAddress ?? {
      street: '',
      number: '',
      city: '',
      province: '',
      postalCode: '',
    },
    zone: customer.zone,
    notes: customer.notes ?? '',
  };
}

/** A PATCH sends only the fields that actually changed (`design.md` §7, orchestrator decision 4). */
function diffChanges(
  customer: CustomerDetail,
  values: CustomerFormValues,
): Partial<CustomerFormInput> {
  const changes: Partial<CustomerFormInput> = {};

  if (values.legalName !== customer.legalName) changes.legalName = values.legalName;
  if (values.tradeName !== customer.tradeName) changes.tradeName = values.tradeName;
  if (values.taxId !== customer.taxId) changes.taxId = values.taxId;
  if (values.taxCondition !== customer.taxCondition) changes.taxCondition = values.taxCondition;
  if (values.billingEmail !== customer.billingEmail) changes.billingEmail = values.billingEmail;
  if (values.zone !== customer.zone) changes.zone = values.zone;

  const newNotes = values.notes === '' ? null : values.notes;
  if (newNotes !== customer.notes) changes.notes = values.notes;

  const hasAddress = Object.values(values.billingAddress).some((value) => value !== '');
  const newAddress = hasAddress ? values.billingAddress : null;

  if (JSON.stringify(newAddress) !== JSON.stringify(customer.billingAddress) && hasAddress) {
    changes.billingAddress = values.billingAddress;
  }

  return changes;
}

interface SuspendDialogProps {
  submitting: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}

function SuspendDialog({ submitting, onCancel, onConfirm }: SuspendDialogProps): JSX.Element {
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);

  const trimmed = reason.trim();
  const showError = touched && trimmed === '';

  function handleSubmit(event: FormEvent): void {
    event.preventDefault();
    setTouched(true);

    if (trimmed === '') {
      return;
    }

    onConfirm(trimmed);
  }

  return (
    <Modal open onClose={onCancel} title="Suspender empresa" className="max-w-md">
      <form onSubmit={handleSubmit} className="flex flex-col gap-3" noValidate>
        <label htmlFor="suspend-reason" className="text-sm font-medium text-slate-900">
          Motivo
          <span aria-hidden="true" className="ml-0.5 text-red-600">
            *
          </span>
        </label>
        <textarea
          id="suspend-reason"
          autoFocus
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          aria-invalid={showError}
          aria-describedby={showError ? 'suspend-reason-message' : undefined}
          className={`min-h-24 rounded-md border px-3 py-2 text-sm ${showError ? 'border-red-500' : 'border-slate-300'}`}
        />
        {showError ? (
          <p id="suspend-reason-message" role="alert" className="text-xs text-red-600">
            El motivo es obligatorio
          </p>
        ) : null}

        <div className="mt-2 flex items-center gap-3">
          <Button type="submit" variant="destructive" loading={submitting}>
            Suspender
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancelar
          </Button>
        </div>
      </form>
    </Modal>
  );
}
